import { useState, useEffect } from "react";
import API from "@/lib/api";
import { FileCheck, AlertTriangle } from "lucide-react";

/**
 * Compact BAA (Business Associate Agreement) compliance summary — pulled in at
 * the bottom of the Admin Overview tab. Shows counts of active/expiring/expired
 * BAAs plus a per-associate breakdown. Click-through goes to the full BAA tab.
 */
export default function BAAComplianceWidget({ onNavigate }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/baa/summary").then(({ data }) => setSummary(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  const hasIssues = summary && (summary.expired > 0 || summary.expiring_soon > 0);

  return (
    <div className={`mt-6 rounded-xl border p-5 ${hasIssues ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="baa-compliance-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileCheck className={`w-5 h-5 ${hasIssues ? "text-amber-600" : "text-emerald-600"}`} />
          <h3 className="font-semibold text-slate-900 dark:text-white text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>BAA Compliance</h3>
        </div>
        <button onClick={onNavigate} className="text-xs text-purple-600 hover:text-purple-700 font-medium" data-testid="baa-widget-manage-btn">
          Manage BAAs &rarr;
        </button>
      </div>
      {!summary || summary.total === 0 ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          <span>No BAAs on file. Upload signed agreements for compliance tracking.</span>
        </div>
      ) : (
        <div>
          <div className="flex gap-3 mb-3">
            {summary.active > 0 && <span className="text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full px-2.5 py-1" data-testid="baa-active-count">{summary.active} Active</span>}
            {summary.expiring_soon > 0 && <span className="text-xs font-medium bg-amber-100 text-amber-700 rounded-full px-2.5 py-1" data-testid="baa-expiring-count">{summary.expiring_soon} Expiring Soon</span>}
            {summary.expired > 0 && <span className="text-xs font-medium bg-red-100 text-red-700 rounded-full px-2.5 py-1" data-testid="baa-expired-count">{summary.expired} Expired</span>}
            {summary.no_expiration > 0 && <span className="text-xs font-medium bg-slate-100 text-slate-600 dark:text-slate-300 rounded-full px-2.5 py-1">{summary.no_expiration} No Exp. Set</span>}
          </div>
          <div className="space-y-1.5">
            {summary.associates.map((a) => {
              const badgeCls = a.status === "active" ? "bg-emerald-100 text-emerald-700" : a.status === "expiring_soon" ? "bg-amber-100 text-amber-700" : a.status === "expired" ? "bg-red-100 text-red-700" : a.status === "revoked" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600 dark:text-slate-300";
              const label = a.status === "active" ? "Active" : a.status === "expiring_soon" ? "Expiring" : a.status === "expired" ? "Expired" : a.status === "revoked" ? "Revoked" : "No Exp.";
              return (
                <div key={a.id} className="flex items-center justify-between text-xs">
                  <span className="text-slate-700 dark:text-slate-200 font-medium">{a.party_name}</span>
                  <div className="flex items-center gap-2">
                    {a.expiration_date && <span className="text-slate-400 dark:text-slate-500">{new Date(a.expiration_date + "T00:00:00").toLocaleDateString()}</span>}
                    <span className={`px-2 py-0.5 rounded-full font-medium ${badgeCls}`}>{label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
